import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useToast } from "../../Common/Components/ToastProvider";
import LoadingSpinner from "../../Common/Components/LoadingSpinner";
import "../../Common/CommonCss/AuthPage.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

const Register = () => {
  // password states ( type password or text )
  const [passwordState, setPasswordState] = useState(false);
  const [confirmPasswordState, setConfirmPasswordState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registerData, setRegisterData] = useState({
    name: "",
    email: "",
    phone: "",
    card: "",
    agent_code: "",
    password: "",
    confirmPassword: "",
  });

  const navigate = useNavigate();
  const { showToast } = useToast();

  const handleChange = (e) => {
    setRegisterData({
      ...registerData,
      [e.target.name]: e.target.value,
    });
  };

  // validate form
  const validateForm = () => {
    if (
      !registerData.name ||
      !registerData.email ||
      !registerData.phone ||
      !registerData.card ||
      !registerData.password ||
      !registerData.confirmPassword ||
      !registerData.agent_code
    ) {
      showToast("Please fill in all required fields", "error");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registerData.email)) {
      showToast("Please enter a valid email address", "error");
      return false;
    }

    if (registerData.phone.length !== 10 || !/^\d+$/.test(registerData.phone)) {
      showToast("Phone number must be 10 digits", "error");
      return false;
    }

    if (registerData.card.length !== 16 || !/^\d+$/.test(registerData.card)) {
      showToast("Card number must be 16 digits", "error");
      return false;
    }

    if (registerData.password !== registerData.confirmPassword) {
      showToast("Passwords do not match", "error");
      return false;
    }

    if (registerData.password.length < 8) {
      showToast("Password must be at least 8 characters", "error");
      return false;
    }

    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetchWithTimeout(
        `${process.env.REACT_APP_URL}/api/customer/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: registerData.name,
            email: registerData.email,
            phone: registerData.phone,
            card: registerData.card,
            password: registerData.password,
            agent_code: registerData.agent_code,
          }),
        },
        30000
      );

      const data = await response.json();
      if (data.status === true) {
        showToast(
          data.message || "Registration successful! Redirecting to login...",
          "success"
        );
        setTimeout(() => {
          navigate("/login");
        }, 1500);
      } else {
        showToast(
          data.message || "Registration failed. Please try again.",
          "error"
        );
        setLoading(false);
      }
    } catch (error) {
      console.error("Registration error: ", error);
      if (error.message === 'Request timeout - Server took too long to respond') {
        showToast("Request timeout - Please try again or check your connection", "error");
      } else {
        showToast("Network error. Please try again", "error");
      }
      setLoading(false);
    }
  };

  // password state toggle functions
  const togglePasswordState = () => {
    setPasswordState((prev) => !prev);
  };
  const toggleConfirmPasswordState = () => {
    setConfirmPasswordState((prev) => !prev);
  };

  return (
    <>
      {loading && <LoadingSpinner />}
      <div className="auth_wrapper">
        <div className="auth_container">
          <h1>Register</h1>

          <form onSubmit={handleRegister}>
            <div className="form_group">
              <label htmlFor="name">Name</label>
              <div className="input_wrapper">
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={registerData.name}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Enter your Name here"
                />
              </div>
            </div>
            <div className="form_group">
              <label htmlFor="email">Email</label>
              <div className="input_wrapper">
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={registerData.email}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Enter your Email here"
                />
              </div>
            </div>
            <div className="form_group">
              <label htmlFor="phone">Phone</label>
              <div className="input_wrapper">
                <input
                  type="text"
                  name="phone"
                  id="phone"
                  value={registerData.phone}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Enter your Phone here"
                />
              </div>
            </div>
            <div className="form_group">
              <label htmlFor="card">Card</label>
              <div className="input_wrapper">
                <input
                  type="text"
                  name="card"
                  id="card"
                  value={registerData.card}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Enter your Card here"
                />
              </div>
            </div>
            <div className="form_group">
              <label htmlFor="agent_code">Refer Code</label>
              <div className="input_wrapper">
                <input
                  type="text"
                  name="agent_code"
                  id="agent_code"
                  value={registerData.agent_code}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Enter your Refer Code here"
                />
              </div>
            </div>
            <div className="form_group">
              <label htmlFor="password">Password</label>
              <div className="input_wrapper">
                <input
                  type={passwordState ? "text" : "password"}
                  name="password"
                  id="password"
                  value={registerData.password}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Enter your Password here"
                />
                <span
                  className="password_toggle_icon"
                  onClick={togglePasswordState}
                >
                  {passwordState ? <FaEye /> : <FaEyeSlash />}
                </span>
              </div>
            </div>
            <div className="form_group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="input_wrapper">
                <input
                  type={confirmPasswordState ? "text" : "password"}
                  name="confirmPassword"
                  id="confirmPassword"
                  value={registerData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  placeholder="Confirm your Password here"
                />
                <span
                  className="password_toggle_icon"
                  onClick={toggleConfirmPasswordState}
                >
                  {confirmPasswordState ? <FaEye /> : <FaEyeSlash />}
                </span>
              </div>
            </div>
            <div className="form_group">
              <p className="existing_account">
                Already have an account ? <Link to="/login">Login</Link>
              </p>
            </div>
            <div className="form_group">
              <button className="primary_button btn full_width" type="submit">
                {loading ? "Registering..." : "Register"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Register;
